import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { collectionQueryOptions } from '@/features/collections';
import { CollectionPicker } from '@/features/resource-pickers';

export interface CollectionFieldProps {
  storeId: string;
  value: string;
  onChange: (id: string) => void;
}

/**
 * The chosen collection of the store and a button that opens the shared collection picker.
 * Hidden collections are labelled so a rail is not left empty by surprise.
 */
export function CollectionField({ storeId, value, onChange }: CollectionFieldProps) {
  const { t } = useTranslation('content');
  const [open, setOpen] = useState(false);
  const query = useQuery({ ...collectionQueryOptions(storeId, value), enabled: value !== '' });
  const collection = value ? query.data : undefined;

  return (
    <div className="flex items-center gap-2">
      <span className="flex min-w-0 flex-1 items-center gap-2 text-sm">
        {value === '' ? (
          <span className="text-muted-foreground">{t('rail.noCollection')}</span>
        ) : query.isPending ? (
          <Skeleton className="h-5 w-40" />
        ) : (
          <>
            <span className="truncate">{collection?.title ?? value}</span>
            {collection?.status === 'hidden' ? (
              <Badge variant="outline" className="text-muted-foreground font-normal">
                {t('rail.hiddenCollection')}
              </Badge>
            ) : null}
          </>
        )}
      </span>
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
        {value ? t('rail.changeCollection') : t('rail.pickCollection')}
      </Button>
      <CollectionPicker
        open={open}
        onOpenChange={setOpen}
        storeId={storeId}
        initialSelectedIds={value ? [value] : []}
        onConfirm={(selection) => {
          const [id] = selection.ids;
          if (id) onChange(id);
        }}
      />
    </div>
  );
}
